/**
 * src/lib/capabilities.ts
 *
 * Shared capability definitions for the frontend chat experience.
 * This mirrors supabase/functions/_shared/capabilities.ts — keep both in sync.
 *
 * Used by Chat.tsx and any future frontend chat channel components.
 */

export interface CapabilityItem {
  icon: string;
  text: string;
}

export interface CapabilityCategory {
  title: string;
  emoji: string;
  items: CapabilityItem[];
}

/**
 * All platform capabilities grouped by category.
 * Shown on the welcome screen and used for contextual follow-up suggestions.
 */
export const CAPABILITY_CATEGORIES: CapabilityCategory[] = [
  {
    title: "Dashboards & Visualization",
    emoji: "📊",
    items: [
      { icon: "📊", text: "Build a sales performance dashboard" },
      { icon: "📈", text: "Visualize trends over time" },
      { icon: "🎯", text: "Create a KPI tracker for my team" },
    ],
  },
  {
    title: "Reports & Scheduling",
    emoji: "📋",
    items: [
      { icon: "📝", text: "Generate a weekly summary report" },
      { icon: "🕐", text: "Schedule automated daily reports" },
      { icon: "📄", text: "Create a custom report template" },
    ],
  },
  {
    title: "Alerts & Monitoring",
    emoji: "🔔",
    items: [
      { icon: "🚨", text: "Set up anomaly alerts for sales drops" },
      { icon: "📉", text: "Alert me when a metric drops below threshold" },
      { icon: "🔍", text: "Monitor key business metrics for unusual patterns" },
    ],
  },
  {
    title: "Customer Intelligence",
    emoji: "👥",
    items: [
      { icon: "👤", text: "Analyze my customer segments" },
      { icon: "💰", text: "Get customer lifetime value insights" },
      { icon: "⚠️", text: "Identify at-risk customers likely to churn" },
    ],
  },
  {
    title: "AI Agents & Workflows",
    emoji: "🤖",
    items: [
      { icon: "🤖", text: "Build an AI agent to analyze data automatically" },
      { icon: "⚡", text: "Create a workflow to process new data as it arrives" },
      { icon: "🔗", text: "Set up an automated insights pipeline" },
    ],
  },
];

/**
 * Flat list of capability follow-up suggestions for contextual use
 * when the conversation response is exploratory / non-data.
 */
export const CAPABILITY_FOLLOWUPS: CapabilityItem[] = [
  { icon: "📊", text: "Build a dashboard from my data" },
  { icon: "📋", text: "Schedule an automated report" },
  { icon: "🔔", text: "Set up anomaly alerts for key metrics" },
  { icon: "👥", text: "Analyze my customer segments" },
  { icon: "🤖", text: "Create an AI workflow to automate insights" },
  { icon: "📈", text: "Show me a trend analysis" },
  { icon: "💡", text: "What else can you help me with?" },
];

/**
 * Returns true when the assistant response looks like a general/informational
 * answer rather than a data-query result (no SQL, tables, numbers-heavy content).
 * Used by FollowUpSuggestions to decide whether to show capability chips.
 */
export function isGeneralResponse(content: string): boolean {
  const lower = content.toLowerCase();
  // If response contains SQL, tabular data, or heavy numbers it's data-oriented
  if ((content.match(/```/g) ?? []).length >= 2) return false;
  if (lower.includes("select ") && lower.includes("from ")) return false;
  if ((content.match(/\|/g) ?? []).length > 6) return false;
  // If no data indicators, it's likely a general/informational response
  const dataTerms = ["total", "revenue", "count", "average", "sum", "rows", "records"];
  const dataHits = dataTerms.filter((t) => lower.includes(t)).length;
  return dataHits < 2;
}
